/**
 * Admin → Holiday calendar (HR, `holidays.manage`). Not in the prototype — the HR role was added by operator
 * decision 8 (ADR-005) — so it is built from the prototype's own parts: page head, stat strip, filter pills,
 * panel tables, pop-ups.
 *
 *  - Each year's calendar: the seeded Maharashtra list arrives as a DRAFT; HR checks it, adds/edits/removes
 *    fixed, optional and half days, sets how many optional holidays each person may take, then
 *    "Check and publish" (GET …/publish-preview shows the blast radius, POST …/publish commits).
 *  - A published calendar is never edited in place: "Amend this calendar" starts a new draft copied from it,
 *    which supersedes it on publish.
 *  - Optional holidays taken: per person, what they elected, used and left (GET /optional-holidays), and
 *    "Take for them" (POST /users/{id}/optional-holidays).
 *  - Day types: what each kind of day does to expected hours (GET/POST/PATCH /holiday-day-types).
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { HolidayCalendar as Cal, HolidayDay, HolidayDayType } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { useSession } from '../../lib/session';
import { firstName, todayIso } from '../../lib/format';
import { hkeys, shortDay, useCalendar, useCalendars, useDayTypes, useHolidayWrite, useOptionalAdmin } from '../../lib/timesheets';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Empty, ErrorState, Field, Loading, Modal, MultiSelectFilter, Pills, errorText, useToast } from '../../ui/kit';
import { PAGE_SUB } from '../../shell/copy';
import { PageInfo } from '../../shell/Help';
import { Stat } from '../timesheets/TimesheetReport';

type Tab = 'days' | 'optional' | 'types';
const STATUS_CHIP: Record<Cal['status'], [string, string]> = {
  draft: ['warm', 'Draft'],
  published: ['hot', 'Published'],
  superseded: ['', 'Superseded'],
};
const typeChip = (k: string) => (k === 'fixed' ? 'cold' : k === 'optional' ? 'info' : k === 'half_day' ? 'warm' : '');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function HolidayCalendar() {
  const [sp, setSp] = useSearchParams();
  const cals = useCalendars();
  const types = useDayTypes();
  const [creating, setCreating] = useState(false);
  const list = useMemo(() => [...(cals.data?.data ?? [])].sort((a, b) => b.year - a.year || (a.status === 'published' ? -1 : 1)), [cals.data]);
  const visible = list.filter((c) => c.status !== 'superseded');
  const pick = sp.get('cal');
  const cur = visible.find((c) => c.id === pick) ?? visible.find((c) => c.year === Number(todayIso().slice(0, 4)) && c.status === 'draft') ?? visible.find((c) => c.year === Number(todayIso().slice(0, 4))) ?? visible[0];
  const set = (k: string, v: string) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set(k, v);
      return n;
    });
  const tab = (['days', 'optional', 'types'].includes(sp.get('tab') ?? '') ? sp.get('tab') : 'days') as Tab;

  return (
    <>
      <div className="ph">
        <div>
          <h1>
            Holiday calendar
            <PageInfo helpKey="holidays" />
          </h1>
          <p>{PAGE_SUB['holidays'] ?? 'The yearly holiday calendar every working-day count uses.'}</p>
        </div>
        <div className="r">
          <button className="btn pri" onClick={() => setCreating(true)}>
            Start a year
          </button>
        </div>
      </div>
      {cals.isLoading || types.isLoading ? (
        <Loading />
      ) : cals.error ? (
        <ErrorState error={cals.error} onRetry={() => void cals.refetch()} />
      ) : !cur ? (
        <div className="panel">
          <Empty title="No holiday calendar yet">Start a year to add its holidays, then publish it so working days and timesheets use it.</Empty>
        </div>
      ) : (
        <>
          <div className="bar">
            {/* One calendar is shown at a time, so this is single-select by design (t-db534cf7 audit #16). It always has a pick. */}
            <MultiSelectFilter
              label="Calendar"
              single
              required
              options={visible.map((c) => ({ value: c.id, label: `${c.year} · ${STATUS_CHIP[c.status][1]}${c.status === 'published' && c.published_at ? ` ${shortDay(c.published_at.slice(0, 10))}` : ''}` }))}
              value={[cur.id]}
              onChange={(v) => {
                if (v[0]) set('cal', v[0]);
              }}
            />
          </div>
          <CalendarView cal={cur} types={types.data?.data ?? []} tab={tab} setTab={(t) => set('tab', t)} drafts={list} onPick={(id) => set('cal', id)} />
        </>
      )}
      {creating ? (
        <StartYearModal
          calendars={list}
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            set('cal', c.id);
          }}
        />
      ) : null}
    </>
  );
}

function CalendarView({ cal, types, tab, setTab, drafts, onPick }: { cal: Cal; types: HolidayDayType[]; tab: Tab; setTab: (t: Tab) => void; drafts: Cal[]; onPick: (id: string) => void }) {
  const { me } = useSession();
  const full = useCalendar(cal.id);
  const toast = useToast();
  const [previewing, setPreviewing] = useState(false);
  const [allow, setAllow] = useState(String(cal.optional_allowance));
  useEffect(() => setAllow(String(cal.optional_allowance)), [cal.optional_allowance, cal.id]);
  const draft = cal.status === 'draft';
  const days = useMemo(() => [...(full.data?.days ?? [])].sort((a, b) => a.the_date.localeCompare(b.the_date)), [full.data]);
  const label = (k: string) => types.find((t) => t.key === k)?.label ?? k;
  const n = (k: string) => days.filter((d) => d.day_type === k).length;
  const pendingAmend = drafts.find((c) => c.year === cal.year && c.region === cal.region && c.status === 'draft' && c.id !== cal.id);

  const saveAllow = useHolidayWrite(() => call('holidays.update', { params: { id: cal.id }, body: { optional_allowance: Number(allow) }, ifMatch: full.data?.version ?? cal.version }));
  const amend = useHolidayWrite(() =>
    call('holidays.create', { body: { year: cal.year, region: cal.region, optional_allowance: cal.optional_allowance, copy_from_id: cal.id, note: cal.published_at ? `Amendment of the calendar published ${shortDay(cal.published_at.slice(0, 10))}` : 'Amendment of the published calendar' }, idempotencyKey: newIdempotencyKey() }),
  );

  return (
    <>
      <div className="stats">
        <Stat k="Status" v={STATUS_CHIP[cal.status][1]} s={cal.published_at ? `by ${cal.published_by ? firstName(cal.published_by.name) : 'HR'}, ${shortDay(cal.published_at.slice(0, 10))}` : 'nobody’s hours change until it is published'} cls={cal.status === 'published' ? 'green' : 'amber'} />
        <Stat k="Holidays" v={n('fixed')} s={`${cal.region} list for ${cal.year}`} />
        <Stat k="Optional holidays" v={n('optional')} s={`each person may take ${cal.optional_allowance}`} />
        <Stat k="Working day" v={`${me.config.working_day_hours} h`} s="expected hours, set in Settings" />
      </div>

      {draft ? (
        <div className="note" style={{ marginBottom: 16 }}>
          <b>This is a draft.</b> {cal.note ?? 'Check every date against the official notification, then publish it.'} Working days, the timesheet gate and expected hours only use a published calendar.
          <div className="split" style={{ gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <span className="sub">Optional holidays each person may take</span>
            <input className="in mins" type="number" min={0} max={20} value={allow} onChange={(e) => setAllow(e.target.value)} aria-label="Optional holidays each person may take" />
            <button
              className="btn sm"
              disabled={saveAllow.isPending || allow === String(cal.optional_allowance)}
              onClick={() => saveAllow.mutate(undefined, { onSuccess: () => toast('Allowance saved'), onError: (e) => toast(errorText(e)) })}
            >
              Save
            </button>
            <div className="grow" />
            <button className="btn pri" onClick={() => setPreviewing(true)}>
              Check and publish
            </button>
          </div>
        </div>
      ) : cal.status === 'published' ? (
        <div className="split" style={{ marginBottom: 16, gap: 10 }}>
          <div className="sub" style={{ fontSize: 13 }}>
            Published calendars are not edited in place. An amendment is a new draft that replaces this one when you publish it.
          </div>
          <div className="grow" />
          {pendingAmend ? (
            <button className="btn" onClick={() => onPick(pendingAmend.id)}>
              Open the amendment draft
            </button>
          ) : (
            <button
              className="btn"
              disabled={amend.isPending}
              onClick={() => amend.mutate(undefined, { onSuccess: (c) => (toast(`Amendment draft started for ${cal.year}`), onPick(c.id)), onError: (e) => toast(errorText(e)) })}
            >
              Amend this calendar
            </button>
          )}
        </div>
      ) : null}

      <div className="bar" style={{ marginTop: 0 }}>
        <Pills<Tab>
          options={[
            ['days', 'Days', days.length],
            ['optional', 'Optional holidays taken'],
            ['types', 'Day types'],
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>
      {tab === 'days' ? (
        full.isLoading ? (
          <Loading />
        ) : full.error ? (
          <ErrorState error={full.error} onRetry={() => void full.refetch()} />
        ) : (
          <DaysTable cal={cal} days={days} types={types} label={label} />
        )
      ) : tab === 'optional' ? (
        <OptionalTaken year={cal.year} />
      ) : (
        <DayTypes types={types} />
      )}
      {previewing ? <PublishModal cal={cal} onClose={() => setPreviewing(false)} /> : null}
    </>
  );
}

function DaysTable({ cal, days, types, label }: { cal: Cal; days: HolidayDay[]; types: HolidayDayType[]; label: (k: string) => string }) {
  const draft = cal.status === 'draft';
  const toast = useToast();
  const active = types.filter((t) => t.active);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState(active[0]?.key ?? 'fixed');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<HolidayDay | null>(null);
  const add = useHolidayWrite(() => call('holidays.addDay', { params: { id: cal.id }, body: { the_date: date, name: name.trim(), day_type: type }, idempotencyKey: newIdempotencyKey() }));
  const del = useHolidayWrite((d: HolidayDay) => call('holidays.deleteDay', { params: { id: cal.id, day_id: d.id } }));
  const submit = () => {
    const e: Record<string, string> = {};
    if (!date) e['body.the_date'] = 'Pick the date';
    else if (!date.startsWith(String(cal.year))) e['body.the_date'] = `Pick a date in ${cal.year}`;
    if (name.trim().length < 2) e['body.name'] = 'Name the holiday';
    setErrs(e);
    if (Object.keys(e).length) return;
    add.mutate(undefined, {
      onSuccess: (d) => {
        toast(`${d.name} added, ${shortDay(d.the_date)}`);
        setDate('');
        setName('');
      },
      onError: (er) => {
        if (er instanceof ApiFailure) setErrs(er.fieldErrors);
        toast(errorText(er));
      },
    });
  };

  // Month by month, like a wall calendar.
  const byMonth = new Map<number, HolidayDay[]>();
  for (const d of days) {
    const m = Number(d.the_date.slice(5, 7)) - 1;
    byMonth.set(m, [...(byMonth.get(m) ?? []), d]);
  }

  return (
    <>
      {draft ? (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="hd">
            <h3>Add a day</h3>
          </div>
          <div className="bd split" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ width: 170 }}>
              <Field label="Date" error={errs['body.the_date']}>
                <input className="in" type="date" min={`${cal.year}-01-01`} max={`${cal.year}-12-31`} value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
            </div>
            <div style={{ flex: 2, minWidth: 200 }}>
              <Field label="Name" error={errs['body.name']}>
                <input className="in" placeholder="e.g. Diwali (Lakshmi Pujan)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
              </Field>
            </div>
            <div style={{ width: 190 }}>
              <Field label="Type" error={errs['body.day_type']}>
                <select className="in" value={type} onChange={(e) => setType(e.target.value)}>
                  {active.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <button className="btn pri" style={{ marginTop: 22 }} onClick={submit} disabled={add.isPending}>
              Add
            </button>
          </div>
        </div>
      ) : null}
      <div className="panel" style={{ overflowX: 'auto' }}>
        {days.length ? (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Holiday</th>
                <th>Type</th>
                <th>Note</th>
                {draft ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {[...byMonth].map(([m, ds]) => [
                <tr key={`m${m}`}>
                  <td colSpan={draft ? 5 : 4} className="lbl" style={{ background: 'var(--canvas)', padding: '8px 14px' }}>
                    {MONTHS[m]}
                  </td>
                </tr>,
                ...ds.map((d) => (
                  <tr key={d.id}>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      <b>{shortDay(d.the_date)}</b>
                    </td>
                    <td>{d.name}</td>
                    <td>
                      <span className={`chip ${typeChip(d.day_type)}`}>{label(d.day_type)}</span>
                    </td>
                    <td className="sub">{d.note ?? ''}</td>
                    {draft ? (
                      <td className="tr" style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn sm" onClick={() => setEditing(d)}>
                          Edit
                        </button>{' '}
                        <button
                          className="btn sm ghost"
                          disabled={del.isPending}
                          onClick={() => del.mutate(d, { onSuccess: () => toast(`${d.name} removed`), onError: (e) => toast(errorText(e)) })}
                        >
                          Remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        ) : (
          <Empty title="No days in this calendar yet">{draft ? 'Add the holidays above.' : 'This calendar has no holidays.'}</Empty>
        )}
      </div>
      {editing ? <EditDayModal cal={cal} day={editing} types={active} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function EditDayModal({ cal, day, types, onClose }: { cal: Cal; day: HolidayDay; types: HolidayDayType[]; onClose: () => void }) {
  const toast = useToast();
  const [date, setDate] = useState(day.the_date);
  const [name, setName] = useState(day.name);
  const [type, setType] = useState(day.day_type);
  const [note, setNote] = useState(day.note ?? '');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const save = useHolidayWrite(() =>
    call('holidays.updateDay', {
      params: { id: cal.id, day_id: day.id },
      body: {
        ...(date !== day.the_date ? { the_date: date } : {}),
        ...(name.trim() !== day.name ? { name: name.trim() } : {}),
        ...(type !== day.day_type ? { day_type: type } : {}),
        ...(note.trim() !== (day.note ?? '') ? { note: note.trim() } : {}),
      },
    }),
  );
  const changed = date !== day.the_date || name.trim() !== day.name || type !== day.day_type || note.trim() !== (day.note ?? '');
  return (
    <Modal
      title={`Edit ${day.name}`}
      sub={`${cal.year} draft. Nothing changes for anyone until the calendar is published.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={!changed || save.isPending}
            onClick={() =>
              save.mutate(undefined, {
                onSuccess: () => (toast('Saved'), onClose()),
                onError: (e) => {
                  if (e instanceof ApiFailure) setErrs(e.fieldErrors);
                  toast(errorText(e));
                },
              })
            }
          >
            Save
          </button>
        </>
      }
    >
      <div className="cols c2">
        <Field label="Date" error={errs['body.the_date']}>
          <input className="in" type="date" min={`${cal.year}-01-01`} max={`${cal.year}-12-31`} value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Type" error={errs['body.day_type']}>
          <select className="in" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Name" error={errs['body.name']}>
        <input className="in" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Note" error={errs['body.note']}>
        <input className="in" placeholder="Optional" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

function PublishModal({ cal, onClose }: { cal: Cal; onClose: () => void }) {
  const toast = useToast();
  const pv = useQuery({ queryKey: hkeys.preview(cal.id), queryFn: () => call('holidays.publishPreview', { params: { id: cal.id } }), staleTime: 0 });
  const pub = useHolidayWrite(() => call('holidays.publish', { params: { id: cal.id }, idempotencyKey: newIdempotencyKey() }));
  const p = pv.data;
  return (
    <Modal
      wide
      title={`Publish the ${cal.year} calendar`}
      sub="Check what changes before it does. Publishing is recorded with your name."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={!p?.can_publish || pub.isPending}
            onClick={() =>
              pub.mutate(undefined, {
                onSuccess: (r) => {
                  toast(`${cal.year} calendar published. ${r.affected_users} ${r.affected_users === 1 ? 'person’s' : 'people’s'} expected hours updated.`);
                  onClose();
                },
                onError: (e) => toast(errorText(e)),
              })
            }
          >
            Publish
          </button>
        </>
      }
    >
      {pv.isLoading ? (
        <Loading label="Checking the calendar…" />
      ) : pv.error ? (
        <ErrorState error={pv.error} onRetry={() => void pv.refetch()} />
      ) : p ? (
        <>
          {p.field_errors.length ? (
            <div className="note bad" style={{ marginBottom: 14 }}>
              <b>Fix these before publishing</b>
              <ul style={{ margin: '8px 0 0 18px' }}>
                {p.field_errors.map((f, i) => (
                  <li key={i}>{f.message}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="note" style={{ marginBottom: 14 }}>
              <b>Ready to publish.</b> {p.supersedes_id ? 'It replaces the calendar published now for this year.' : 'It becomes the calendar for this year.'}
            </div>
          )}
          <div className="stats">
            <Stat k="Dates that change" v={p.affected_dates.length} s="expected hours differ" />
            <Stat k="People affected" v={p.affected_users} />
            <Stat k="Optional holidays kept" v={p.carried_elections} s="move to the new calendar" />
            <Stat k="Optional holidays withdrawn" v={p.withdrawn_elections.length} cls={p.withdrawn_elections.length ? 'amber' : ''} s={p.withdrawn_elections.length ? 'those people owe that day again' : ''} />
          </div>
          {p.affected_dates.length ? (
            <div className="sub" style={{ fontSize: 13 }}>
              Dates: {p.affected_dates.slice(0, 24).map(shortDay).join(', ')}
              {p.affected_dates.length > 24 ? ` and ${p.affected_dates.length - 24} more` : ''}
            </div>
          ) : null}
          {p.withdrawn_elections.length ? (
            <table style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Day</th>
                  <th>Next day locked again</th>
                </tr>
              </thead>
              <tbody>
                {p.withdrawn_elections.map((w, i) => (
                  <tr key={i}>
                    <td>{w.user.name}</td>
                    <td>
                      {w.name}, {shortDay(w.date)}
                    </td>
                    <td>{w.re_gated ? <span className="chip cold">Yes, until they file it</span> : <span className="sub">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

function OptionalTaken({ year }: { year: number }) {
  const q = useOptionalAdmin(year);
  const cals = useCalendars();
  const pubId = cals.data?.data.find((c) => c.year === year && c.status === 'published')?.id ?? null;
  const pub = useCalendar(pubId);
  const toast = useToast();
  const [pick, setPick] = useState<Record<string, string>>({});
  const elect = useHolidayWrite((v: { userId: string; dayId: string }) =>
    call('optionalHolidays.electFor', { params: { id: v.userId }, body: { holiday_day_id: v.dayId }, idempotencyKey: newIdempotencyKey() }),
  );
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const optDays = (pub.data?.days ?? []).filter((d) => d.day_type === 'optional').sort((a, b) => a.the_date.localeCompare(b.the_date));
  const rows = q.data?.data ?? [];
  return (
    <div className="panel" style={{ overflowX: 'auto' }}>
      {!pubId ? (
        <div className="note" style={{ margin: 16 }}>
          Optional holidays can be taken once the {year} calendar is published.
        </div>
      ) : null}
      {rows.length ? (
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Optional holidays taken</th>
              <th className="tr">Used</th>
              <th className="tr">Left</th>
              {pubId ? <th>Take one for them</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const left = optDays.filter((d) => !r.elected.some((e) => e.id === d.id));
              return (
                <tr key={r.user.id}>
                  <td>
                    <div className="split">
                      <Avatar user={r.user} />
                      <b>{r.user.name}</b>
                    </div>
                  </td>
                  <td>
                    {r.elected.length ? (
                      r.elected.map((d) => (
                        <span key={d.id} className="chip info" style={{ marginRight: 6 }}>
                          {d.name}, {shortDay(d.the_date)}
                        </span>
                      ))
                    ) : (
                      <span className="sub">None yet</span>
                    )}
                  </td>
                  <td className="tr num">{r.used}</td>
                  <td className="tr num">
                    <span className={`chip ${r.remaining ? 'hot' : 'cold'}`}>{r.remaining}</span>
                  </td>
                  {pubId ? (
                    <td>
                      {r.remaining && left.length ? (
                        <div className="split" style={{ gap: 8 }}>
                          <select className="in" style={{ width: 'auto', borderRadius: 99 }} value={pick[r.user.id] ?? ''} onChange={(e) => setPick((x) => ({ ...x, [r.user.id]: e.target.value }))} aria-label={`Optional holiday for ${r.user.name}`}>
                            <option value="">Pick a day</option>
                            {left.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}, {shortDay(d.the_date)}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn sm"
                            disabled={!pick[r.user.id] || elect.isPending}
                            onClick={() =>
                              elect.mutate({ userId: r.user.id, dayId: pick[r.user.id]! }, { onSuccess: () => toast(`Taken for ${firstName(r.user.name)}`), onError: (e) => toast(errorText(e)) })
                            }
                          >
                            Take for them
                          </button>
                        </div>
                      ) : (
                        <span className="sub">{r.remaining ? 'No optional days left to take' : 'Allowance used'}</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Empty title="Nobody to show yet" />
      )}
    </div>
  );
}

const EFFECT: Record<HolidayDayType['expected_hours_effect'], string> = { zero: 'No hours expected', half: 'Half the day’s hours', none: 'A normal day' };

function DayTypes({ types }: { types: HolidayDayType[] }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const upd = useHolidayWrite((v: { key: string; active: boolean }) => call('holidayTypes.update', { params: { key: v.key }, body: { active: v.active } }));
  return (
    <>
      <div className="panel" style={{ overflowX: 'auto' }}>
        <div className="hd">
          <h3>Day types</h3>
          <div className="r">
            <button className="btn sm" onClick={() => setAdding(true)}>
              Add a type
            </button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Expected hours</th>
              <th>Who is off</th>
              <th>Counts against the allowance</th>
              <th>In use</th>
            </tr>
          </thead>
          <tbody>
            {[...types]
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((t) => (
                <tr key={t.key} style={t.active ? undefined : { opacity: 0.6 }}>
                  <td>
                    <span className={`chip ${typeChip(t.key)}`}>{t.label}</span>
                  </td>
                  <td>{t.expected_hours_override != null ? `${t.expected_hours_override} h` : EFFECT[t.expected_hours_effect]}</td>
                  <td>{t.requires_opt_in ? 'Only people who take it' : t.is_working_day ? 'Nobody (a working day)' : 'Everyone'}</td>
                  <td>{t.counts_against_cap ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      disabled={upd.isPending}
                      onClick={() => upd.mutate({ key: t.key, active: !t.active }, { onSuccess: () => toast(t.active ? `${t.label} switched off` : `${t.label} back in use`), onError: (e) => toast(errorText(e)) })}
                    >
                      {t.active ? 'In use · switch off' : 'Off · switch on'}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {adding ? <AddTypeModal onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function AddTypeModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [effect, setEffect] = useState<HolidayDayType['expected_hours_effect']>('zero');
  const [optIn, setOptIn] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^([^a-z])/, 't_$1').slice(0, 31);
  const add = useHolidayWrite(() =>
    call('holidayTypes.create', { body: { key, label: label.trim(), expected_hours_effect: effect, requires_opt_in: optIn, counts_against_cap: optIn, is_working_day: effect !== 'zero' || optIn }, idempotencyKey: newIdempotencyKey() }),
  );
  return (
    <Modal
      title="Add a day type"
      sub="For example a company day off, or a half day before a festival."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={label.trim().length < 2 || add.isPending}
            onClick={() =>
              add.mutate(undefined, {
                onSuccess: (t) => (toast(`${t.label} added`), onClose()),
                onError: (e) => {
                  if (e instanceof ApiFailure) setErrs(e.fieldErrors);
                  toast(errorText(e));
                },
              })
            }
          >
            Add
          </button>
        </>
      }
    >
      <Field label="Name" error={errs['body.label'] ?? errs['body.key']}>
        <input className="in" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Company day off" autoFocus />
      </Field>
      <Field label="Expected hours on that day">
        <select className="in" value={effect} onChange={(e) => setEffect(e.target.value as typeof effect)}>
          <option value="zero">No hours expected</option>
          <option value="half">Half the day’s hours</option>
          <option value="none">A normal day</option>
        </select>
      </Field>
      <label className="split" style={{ gap: 8, fontSize: 13.5 }}>
        <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
        Only for people who choose to take it (counts against their optional allowance)
      </label>
    </Modal>
  );
}

function StartYearModal({ calendars, onClose, onCreated }: { calendars: Cal[]; onClose: () => void; onCreated: (c: Cal) => void }) {
  const toast = useToast();
  const thisYear = Number(todayIso().slice(0, 4));
  const taken = new Set(calendars.filter((c) => c.status !== 'superseded').map((c) => c.year));
  const firstFree = [thisYear, thisYear + 1, thisYear + 2].find((y) => !taken.has(y)) ?? thisYear + 1;
  const [year, setYear] = useState(String(firstFree));
  const [allow, setAllow] = useState('2');
  const [copy, setCopy] = useState(calendars.find((c) => c.status === 'published')?.id ?? '');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const create = useHolidayWrite(() =>
    call('holidays.create', {
      body: { year: Number(year), region: 'MH', optional_allowance: Number(allow), ...(copy ? { copy_from_id: copy } : {}) },
      idempotencyKey: newIdempotencyKey(),
    }),
  );
  return (
    <Modal
      title="Start a year"
      sub="It starts as a draft. Nobody’s hours change until you publish it."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={create.isPending}
            onClick={() =>
              create.mutate(undefined, {
                onSuccess: (c) => (toast(`${c.year} draft started`), onCreated(c)),
                onError: (e) => {
                  if (e instanceof ApiFailure) setErrs(e.fieldErrors);
                  toast(errorText(e));
                },
              })
            }
          >
            Start draft
          </button>
        </>
      }
    >
      <div className="cols c2">
        <Field label="Year" error={errs['body.year']}>
          <input className="in" type="number" min={2020} max={2100} value={year} onChange={(e) => setYear(e.target.value)} />
        </Field>
        <Field label="Optional holidays each person may take" error={errs['body.optional_allowance']}>
          <input className="in" type="number" min={0} max={20} value={allow} onChange={(e) => setAllow(e.target.value)} />
        </Field>
      </div>
      <Field label="Start from" hint="Copies its days, moved to the new year where the date exists. Check each one before publishing.">
        <select className="in" value={copy} onChange={(e) => setCopy(e.target.value)}>
          <option value="">An empty calendar</option>
          {calendars
            .filter((c) => c.status !== 'superseded')
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.year} {c.region} ({c.status})
              </option>
            ))}
        </select>
      </Field>
    </Modal>
  );
}
